// Deal Calc views — list and editor surfaces.
//
// Both views are intentionally thin in slice 6: they hydrate from
// `/api/deals/state/`, render a minimal listing or editor scaffold,
// and stand up the storage adapter so panel code in slices 7–11 can
// dispatch updates without re-implementing the lifecycle.
//
// All DOM work is direct (no MDC components yet) — slice 8+ wraps
// inputs in MDC primitives where the design calls for it.

import {computeDealMetrics, gradeDeal} from './calculations';
import {createBlankDealState, DealMode, IDealState, mergeStateWithTemplate} from './defaults';
import {AcquisitionPanel} from './panels/acquisition';
import {ARVPanel} from './panels/arv';
import {ExitPanel} from './panels/exit';
import {HoldingPanel} from './panels/holding';
import {ModeToggle} from './panels/mode_toggle';
import {RenoPanel} from './panels/reno';
import {DealSlug, DebouncedDealWriter, HTTPDealStorage, IDeal, INewDeal, IStoredState} from './storage';
import {dealBlockFromEditorDeal, renderDealBlock} from '../../ui/dealblock';

// Matches both the flat `/deals/<deal_slug>/` route and the project-
// scoped `/<project_slug>/deals/<deal_slug>/` route. Captures the deal
// slug as the last group; the project slug (when present) is read from
// the template data attribute, not from the URL, so this regex stays
// concerned only with finding the deal.
const DEAL_SLUG_PATH_RE = /\/deals\/([A-Za-z0-9]{5})\/?$/;

// Compare-cohort size bounds (Phase 5E). Compare CTA enables at
// COHORT_MIN selections; multi-select caps at COHORT_MAX. Mirrors the
// backend's `DealCompareView.MIN_COHORT` / `MAX_COHORT` constants.
const COHORT_MIN = 2;
const COHORT_MAX = 6;

export class DealCalcListView {
	private storage: HTTPDealStorage = new HTTPDealStorage();
	private rootEl: HTMLElement | null;
	private bodyEl: HTMLElement | null;
	private emptyEl: HTMLElement | null;
	private projectSlug: string | null = null;
	// Phase 5B comparison foundation: multi-select up to 2 deals →
	// "Compare" CTA appears. Selection is in-memory only; refreshing
	// the page clears it.
	private compareSelection: Set<DealSlug> = new Set();
	private cachedState: IStoredState | null = null;
	private compareBarEl: HTMLElement | null = null;

	constructor() {
		this.rootEl = document.getElementById('id_lb-deal-list');
		this.bodyEl = document.getElementById('id_lb-deal-list-body');
		this.emptyEl = document.getElementById('id_lb-deal-list-empty');
		const ps = this.rootEl && this.rootEl.getAttribute('data-project-slug');
		this.projectSlug = ps && ps.trim().length > 0 ? ps : null;
		void this.hydrate();
	}

	private async hydrate(): Promise<void> {
		const state = await this.storage.loadState({projectSlug: this.projectSlug});
		this.cachedState = state;
		this.render(state);
	}

	private render(state: IStoredState | null): void {
		if (!this.bodyEl) {
			return;
		}
		// Clear stale children (the loading placeholder)
		while (this.bodyEl.firstChild) {
			this.bodyEl.removeChild(this.bodyEl.firstChild);
		}
		if (!state || state.dealOrder.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-deal-list__empty';
			const title = document.createElement('strong');
			title.textContent = 'No deals yet.';
			const body = document.createElement('span');
			body.textContent = 'Click "New deal" to scaffold your first scenario.';
			empty.appendChild(title);
			empty.appendChild(body);
			this.bodyEl.appendChild(empty);
			if (this.emptyEl && this.emptyEl.parentNode) {
				this.emptyEl.parentNode.removeChild(this.emptyEl);
			}
			return;
		}
		// Project-scoped list view: filter to deals attached to that
		// project. Standalone deals + deals attached to other projects
		// drop out — they live on the flat `/deals/` surface.
		const orderedSlugs = this.projectSlug
			? state.dealOrder.filter(s => {
				const d = state.deals[s];
				return !!d && d.projectSlug === this.projectSlug;
			})
			: state.dealOrder;
		if (orderedSlugs.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-deal-list__empty';
			const title = document.createElement('strong');
			title.textContent = this.projectSlug
				? 'No deals on this project yet.'
				: 'No deals yet.';
			const body = document.createElement('span');
			body.textContent = 'Click "New deal" to scaffold your first scenario.';
			empty.appendChild(title);
			empty.appendChild(body);
			this.bodyEl.appendChild(empty);
			if (this.emptyEl && this.emptyEl.parentNode) {
				this.emptyEl.parentNode.removeChild(this.emptyEl);
			}
			return;
		}
		const list = document.createElement('ul');
		list.className = 'lb-deal-list__items';
		orderedSlugs.forEach(slug => {
			const deal = state.deals[slug];
			if (!deal) {
				return;
			}
			// Deal-forward PR B: per-row rendering is the shared DealBlock
			// component (`variant: 'row'`). The href is overridden so a
			// project-scoped list links into the project-scoped editor and
			// a flat list links into the flat editor, regardless of the
			// deal's own attachment. The multi-select checkbox stays
			// uncapped here (bulk delete selects all); the compare bar
			// below independently gates Compare at COHORT_MAX.
			const href = this.projectSlug
				? `/${encodeURIComponent(this.projectSlug)}/deals/${encodeURIComponent(slug)}/`
				: `/deals/${encodeURIComponent(slug)}/`;
			const row = renderDealBlock(dealBlockFromEditorDeal(deal), {
				variant: 'row',
				href,
				compare: {
					checked: this.compareSelection.has(slug),
					onChange: checked => {
						if (checked) {
							this.compareSelection.add(slug);
						} else {
							this.compareSelection.delete(slug);
						}
						this.render(this.cachedState);
					},
				},
			});
			list.appendChild(row);
		});
		this.bodyEl.appendChild(list);
		if (this.emptyEl && this.emptyEl.parentNode) {
			this.emptyEl.parentNode.removeChild(this.emptyEl);
		}
		this.renderCompareBar();
	}

	private deleting: boolean = false;

	private renderCompareBar(): void {
		// Drop the existing bar so we can re-place it deterministically.
		if (this.compareBarEl && this.compareBarEl.parentNode) {
			this.compareBarEl.parentNode.removeChild(this.compareBarEl);
			this.compareBarEl = null;
		}
		if (!this.rootEl || this.compareSelection.size === 0) {
			return;
		}
		const bar = document.createElement('div');
		bar.className = 'lb-deal-list__compare-bar';
		const n = this.compareSelection.size;
		const canCompare = n >= COHORT_MIN && n <= COHORT_MAX;

		const summary = document.createElement('span');
		summary.className = 'lb-deal-list__compare-summary';
		if (n < COHORT_MIN) {
			summary.textContent = `${n} deal selected — pick at least ${COHORT_MIN - n} more to compare`;
		} else if (n > COHORT_MAX) {
			summary.textContent = `${n} deals selected — Compare max is ${COHORT_MAX}, narrow selection`;
		} else {
			summary.textContent = `${n} deal${n === 1 ? '' : 's'} selected`;
		}
		bar.appendChild(summary);

		const compareBtn = document.createElement('button');
		compareBtn.type = 'button';
		compareBtn.className = 'mdc-button mdc-button--raised lb-deal-list__compare-cta';
		compareBtn.disabled = !canCompare || this.deleting;
		const compareLabel = document.createElement('span');
		compareLabel.className = 'mdc-button__label';
		compareLabel.textContent = 'Compare';
		compareBtn.appendChild(compareLabel);
		compareBtn.addEventListener('click', () => {
			if (!canCompare) {
				return;
			}
			const slugs = Array.from(this.compareSelection).map(encodeURIComponent);
			window.location.assign(`/deals/compare/${slugs.join('/')}/`);
		});
		bar.appendChild(compareBtn);

		const deleteBtn = document.createElement('button');
		deleteBtn.type = 'button';
		deleteBtn.className = 'lb-deal-list__bar-btn lb-deal-list__bar-btn--danger';
		deleteBtn.textContent = this.deleting ? 'Deleting…' : 'Delete';
		deleteBtn.disabled = this.deleting;
		deleteBtn.addEventListener('click', () => void this.handleBulkDelete());
		bar.appendChild(deleteBtn);

		const clear = document.createElement('button');
		clear.type = 'button';
		clear.className = 'lb-deal-list__compare-clear';
		clear.textContent = 'Clear';
		clear.disabled = this.deleting;
		clear.addEventListener('click', () => {
			this.compareSelection.clear();
			this.render(this.cachedState);
		});
		bar.appendChild(clear);

		this.rootEl.appendChild(bar);
		this.compareBarEl = bar;
	}

	private async handleBulkDelete(): Promise<void> {
		const slugs = Array.from(this.compareSelection);
		if (slugs.length === 0 || this.deleting) {
			return;
		}
		const msg = slugs.length === 1
			? 'Delete this deal? This cannot be undone.'
			: `Delete ${slugs.length} deals? This cannot be undone.`;
		if (!window.confirm(msg)) {
			return;
		}
		this.deleting = true;
		this.renderCompareBar();
		// Fire all deletes in parallel; persistence errors are
		// swallowed per-slug because the reload below resyncs from
		// /api/deals/state/ which is the source of truth.
		await Promise.all(slugs.map(async s => {
			try {
				await this.storage.deleteDeal(s);
			} catch {
				// fall through
			}
		}));
		this.deleting = false;
		this.compareSelection.clear();
		// Reload state so the list reflects what actually survived.
		const fresh = await this.storage.loadState({projectSlug: this.projectSlug});
		this.cachedState = fresh;
		this.render(fresh);
	}
}


export class DealCalcEditorView {
	private storage: HTTPDealStorage = new HTTPDealStorage();
	private writer: DebouncedDealWriter;
	private rootEl: HTMLElement | null;
	private addressInput: HTMLInputElement | null;
	private metaEl: HTMLElement | null;
	private modeChipEl: HTMLElement | null;
	private parcelChipEl: HTMLElement | null;
	private parcelChipTextEl: HTMLElement | null;
	private summaryEl: HTMLElement | null;
	private deal: IDeal | null = null;
	private state: IDealState = createBlankDealState();
	private acquisitionPanel: AcquisitionPanel | null = null;
	private renoPanel: RenoPanel | null = null;
	private holdingPanel: HoldingPanel | null = null;
	private arvPanel: ARVPanel | null = null;
	private exitPanel: ExitPanel | null = null;
	private modeToggle: ModeToggle | null = null;

	constructor(private slugFromRoute?: string) {
		this.rootEl = document.getElementById('id_lb-deal-editor');
		this.addressInput = document.getElementById('id_lb-deal-editor-address') as HTMLInputElement | null;
		this.metaEl = document.getElementById('id_lb-deal-editor-meta');
		this.modeChipEl = document.getElementById('id_lb-deal-editor-mode-chip');
		this.parcelChipEl = document.getElementById('id_lb-deal-editor-parcel-chip');
		this.parcelChipTextEl = this.parcelChipEl
			? this.parcelChipEl.querySelector<HTMLElement>('.lb-deal-editor__parcel-chip-text')
			: null;
		this.summaryEl = document.getElementById('id_lb-deal-summary-host');
		this.writer = new DebouncedDealWriter(this.storage);
		this.bindHeader();
		void this.hydrate();
	}

	private bindHeader(): void {
		if (!this.addressInput) {
			return;
		}
		this.addressInput.addEventListener('input', () => {
			const next = this.addressInput!.value;
			this.state = {
				...this.state,
				property: {...this.state.property, address: next},
			};
			this.renderHeaderMeta();
			if (this.deal) {
				this.writer.scheduleUpdate(this.deal.slug, {stateJson: this.state});
			}
		});
	}

	private renderHeaderMeta(): void {
		if (this.addressInput && document.activeElement !== this.addressInput) {
			this.addressInput.value = this.state.property.address || '';
		}
		if (this.metaEl) {
			const p = this.state.property;
			const beds = p.beds || '–';
			const baths = p.baths || '–';
			const sqft = (p.sqft || 0).toLocaleString();
			const yr = p.yearBuilt || '–';
			this.metaEl.textContent = `${beds} bd · ${baths} ba · ${sqft} sqft · built ${yr}`;
		}
		if (this.modeChipEl) {
			this.modeChipEl.setAttribute('data-mode', this.state.mode);
			this.modeChipEl.textContent = this.state.mode === 'build' ? 'New Build' : 'Renovation';
		}
		if (this.parcelChipEl) {
			const linked = this.state.source === 'listblox-parcel' && !!this.state.parcelId;
			this.parcelChipEl.style.display = linked ? '' : 'none';
			if (linked && this.parcelChipTextEl) {
				// Pill shows just "Parcel" — the unique ID was making the
				// chip wider than the toolbar could absorb on narrow
				// viewports, forcing horizontal overflow on the whole
				// surface (sticky footer included). The parcel ID is
				// still exposed via tooltip for power-users.
				this.parcelChipTextEl.textContent = 'Parcel';
				const shortId = (this.state.parcelId || '').slice(0, 8);
				if (shortId) {
					this.parcelChipEl.setAttribute('title', `Parcel · ${shortId}`);
				} else {
					this.parcelChipEl.removeAttribute('title');
				}
			}
			// Phase 3c: when the deal is linked to a parcel, make the
			// chip a click target that navigates to the standalone
			// parcel detail page (`/parcels/<uid>/`). Renders as an
			// anchor visually identical to the inert pill — the only
			// indicator is a subtle pointer cursor. Idempotent: we
			// re-set href on every render so dynamic state changes
			// (parcel link/unlink during the session) don't strand a
			// stale URL.
			if (linked && this.state.parcelId) {
				this.parcelChipEl.classList.add('lb-deal-editor__parcel-chip--linkable');
				(this.parcelChipEl as HTMLAnchorElement).href =
					`/parcels/${encodeURIComponent(this.state.parcelId)}/`;
			} else {
				this.parcelChipEl.classList.remove('lb-deal-editor__parcel-chip--linkable');
				this.parcelChipEl.removeAttribute('href');
			}
		}
	}

	private resolveSlug(): string | null {
		if (this.slugFromRoute) {
			return this.slugFromRoute;
		}
		const attr = this.rootEl && this.rootEl.getAttribute('data-deal-slug');
		if (attr) {
			return attr;
		}
		const m = window.location.pathname.match(DEAL_SLUG_PATH_RE);
		return m ? m[1] : null;
	}

	// Project-scoped URL (`/<project_slug>/deals/...`) is signaled by a
	// `data-project-slug` attribute on the editor root, set server-side
	// when the project context is resolved. Standalone deals leave this
	// null and stay on the flat `/deals/...` URL family.
	private resolveProjectSlug(): string | null {
		const v = this.rootEl && this.rootEl.getAttribute('data-project-slug');
		return v && v.trim().length > 0 ? v : null;
	}

	private async hydrate(): Promise<void> {
		// Mount panels immediately with the template state so the editor
		// surface is interactive even if the API or persistence layer is
		// unreachable. The hydration calls below upgrade in place.
		this.applyHydratedState();
		const slug = this.resolveSlug();
		if (slug) {
			const stored = await this.storage.loadState({projectSlug: this.resolveProjectSlug()});
			if (stored && stored.deals[slug]) {
				this.deal = stored.deals[slug];
				this.state = mergeStateWithTemplate(this.deal.stateJson);
				this.applyHydratedState();
				return;
			}
		}
		// New-deal path. Two query params seed the state in priority
		// order: `?profile=<slug>` applies underwriting-thesis defaults
		// (mode, costs, finance, target metrics) — fetched lazily so the
		// editor still works if the profile API is unreachable.
		// `?parcel=<uid>` applies parcel-specific data (address, lot
		// acres) and wins over profile defaults for those fields. Both
		// are optional; either or both can be absent.
		const parcelUid = this.resolveParcelUid();
		const profileSlug = this.resolveProfileSlug();
		let stateSeed: IDealState = createBlankDealState();
		if (profileSlug) {
			const profile = await this.fetchProfile(profileSlug);
			if (profile) {
				stateSeed = this.applyProfileToState(stateSeed, profile);
				this.state = stateSeed;
				this.applyHydratedState();
			}
		}
		let parcelSnapshot: Record<string, any> | null = null;
		if (parcelUid) {
			const parcel = await this.storage.fetchParcel(parcelUid);
			if (parcel) {
				stateSeed = this.applyParcelToState(stateSeed, parcel);
				parcelSnapshot = parcel as unknown as Record<string, any>;
				this.state = stateSeed;
				this.applyHydratedState();
			}
		}
		const projectSlug = this.resolveProjectSlug();
		try {
			const newPayload: INewDeal = {
				title: 'Untitled scenario',
				stateJson: stateSeed,
			};
			if (parcelUid && parcelSnapshot) {
				newPayload.parcelUid = parcelUid;
				newPayload.parcelSnapshotJson = parcelSnapshot;
			}
			if (projectSlug) {
				// Tells the API to create a ProjectBlock(type=DEAL) and
				// link this Deal to it (Phase 5B Q2 — project-attached
				// scope activates only via the project-scoped URL family).
				newPayload.projectSlug = projectSlug;
			}
			const created = await this.storage.createDeal(newPayload);
			this.deal = created;
			this.state = mergeStateWithTemplate(created.stateJson);
			this.applyHydratedState();
			const target = projectSlug
				? `/${encodeURIComponent(projectSlug)}/deals/${encodeURIComponent(created.slug)}/`
				: `/deals/${encodeURIComponent(created.slug)}/`;
			if (window.location.pathname !== target || window.location.search) {
				window.history.replaceState({}, '', target);
			}
		} catch {
			// Persistence unavailable — keep the editor live in
			// memory-only mode so layout review can still happen.
		}
	}

	private resolveParcelUid(): string | null {
		const params = new URLSearchParams(window.location.search);
		const uid = params.get('parcel');
		return uid && uid.trim().length > 0 ? uid : null;
	}

	// Phase 5C: `?profile=<slug>` query param on the new-deal route.
	// Triggers a one-shot fetch + seed of the deal state from the
	// underwriting thesis. Profile is consumed once at creation; the
	// persisted Deal stays decoupled from the profile (the profile can
	// be edited or deleted later without rewriting the deal).
	private resolveProfileSlug(): string | null {
		const params = new URLSearchParams(window.location.search);
		const slug = params.get('profile');
		return slug && slug.trim().length > 0 ? slug : null;
	}

	private async fetchProfile(slug: string): Promise<IDealProfile | null> {
		// Inline GET to keep the dealcalc storage adapter focused on
		// deals. svc.dealProfile.get() lives in `frontend/ts/request.ts`
		// but importing svc here would pull a different layering tier;
		// we use raw fetch instead. Failures fall through to the
		// no-profile path.
		try {
			const rv = await fetch(`/api/deal-profiles/${encodeURIComponent(slug)}/`, {
				method: 'GET',
				headers: {Accept: 'application/json'},
				credentials: 'same-origin',
			});
			if (!rv.ok) {
				return null;
			}
			return await rv.json() as IDealProfile;
		} catch {
			return null;
		}
	}

	// Map a `DealProfile` onto a Deal state. Only the profile fields
	// that have a direct equivalent in the deal state are applied:
	// - mode (`reno` → `reno`, `build` → `build`, `either` → `reno`)
	// - cost / finance assumptions (contingency %, hold months,
	//   financed rate, build $/sqft)
	// - profile bands and target metrics are evaluation criteria, not
	//   deal-state fields, and stay on the profile for Phase 5D
	//   ranked-discovery scoring.
	private applyProfileToState(seed: IDealState, profile: IDealProfile): IDealState {
		const next: IDealState = {...seed};
		if (profile.mode === 'build') {
			next.mode = 'build';
		} else if (profile.mode === 'reno') {
			next.mode = 'reno';
		}
		// `either` leaves the seed default (reno) untouched.
		if (Number.isFinite(profile.contingencyPct)) {
			next.contingencyPct = profile.contingencyPct;
		}
		if (Number.isFinite(profile.holdingMonths)) {
			next.holdMonths = profile.holdingMonths;
		}
		if (Number.isFinite(profile.financeRatePct)) {
			next.financedRatePct = profile.financeRatePct;
		}
		if (Number.isFinite(profile.buildCostPerSqft)) {
			next.buildCostPerSqft = profile.buildCostPerSqft;
		}
		return next;
	}

	// Map a Listblox parcel payload onto a Deal state. Mirrors the
	// React Header.applyParcel() field set, restricted to what the
	// Listblox ParcelSerializer actually exposes (address + acres
	// today; sqft / beds / baths / yearBuilt aren't on the parcel
	// model and stay blank for the user to fill in).
	private applyParcelToState(seed: IDealState, parcel: IParcel): IDealState {
		const next: IDealState = {
			...seed,
			source: 'listblox-parcel',
			parcelId: parcel.uid || null,
			parcelSnapshot: parcel as unknown as Record<string, any>,
			property: {...seed.property},
		};
		if (parcel.streetAddress) {
			next.property.address = String(parcel.streetAddress);
		}
		const acres = Number(parcel.acres);
		if (Number.isFinite(acres) && acres > 0) {
			next.property.lotAcres = acres;
		}
		return next;
	}

	private applyHydratedState(): void {
		this.renderHeaderMeta();
		if (!this.modeToggle) {
			this.modeToggle = new ModeToggle(this);
		} else {
			this.modeToggle.render();
		}
		if (!this.acquisitionPanel) {
			this.acquisitionPanel = new AcquisitionPanel(this);
		} else {
			this.acquisitionPanel.render();
		}
		if (!this.renoPanel) {
			this.renoPanel = new RenoPanel(this);
		} else {
			this.renoPanel.render();
		}
		if (!this.holdingPanel) {
			this.holdingPanel = new HoldingPanel(this);
		} else {
			this.holdingPanel.render();
		}
		if (!this.arvPanel) {
			this.arvPanel = new ARVPanel(this);
		} else {
			this.arvPanel.render();
		}
		if (!this.exitPanel) {
			this.exitPanel = new ExitPanel(this);
		} else {
			this.exitPanel.render();
		}
		this.renderSummary();
	}

	// ModeToggle host hooks
	getMode(): DealMode {
		return this.state.mode;
	}

	dealHasData(): boolean {
		return (this.state.listPrice || 0) > 0
			|| (this.state.purchasePrice || 0) > 0
			|| (this.state.renoTotal || 0) > 0
			|| (this.state.baseARV || 0) > 0;
	}

	setMode(mode: DealMode): void {
		// On switch, flip the contingency default if the user is still on
		// the prior mode's default — preserves explicit overrides.
		const next: Partial<IDealState> = {mode};
		const prevMode = this.state.mode;
		if (prevMode === 'reno' && mode === 'build' && this.state.contingencyPct === 10) {
			next.contingencyPct = 12;
		} else if (prevMode === 'build' && mode === 'reno' && this.state.contingencyPct === 12) {
			next.contingencyPct = 10;
		}
		this.update(next);
		// Re-render every panel since the mode swap reshapes layouts.
		if (this.acquisitionPanel) {
			this.acquisitionPanel.render();
		}
		if (this.renoPanel) {
			this.renoPanel.render();
		}
		if (this.holdingPanel) {
			this.holdingPanel.render();
		}
		if (this.arvPanel) {
			this.arvPanel.render();
		}
		if (this.exitPanel) {
			this.exitPanel.render();
		}
		// Persist the sticky mode preference best-effort.
		void this.storage.setPref('lastMode', mode);
	}

	// Mirrors DealSummary.jsx in the React standalone — grade chip,
	// three primary metrics with positive/negative coloring on profit
	// and ROI, then a 5-cell breakdown row of cost components. Stays
	// in the right-column sticky position (per user preference) rather
	// than the React reference's full-width fixed-bottom strip.
	private renderSummary(): void {
		if (!this.summaryEl) {
			return;
		}
		while (this.summaryEl.firstChild) {
			this.summaryEl.removeChild(this.summaryEl.firstChild);
		}
		const m = computeDealMetrics(this.state);
		const grade = gradeDeal(m.projectedROI);
		const mode = this.state.mode || 'reno';

		// Grade chip
		const chip = document.createElement('div');
		chip.className = `lb-deal-summary__grade lb-deal-summary__grade--${grade.color}`;
		const chipKicker = document.createElement('div');
		chipKicker.className = 'lb-deal-summary__grade-kicker';
		chipKicker.textContent = 'Deal status';
		chip.appendChild(chipKicker);
		const chipLabel = document.createElement('div');
		chipLabel.className = 'lb-deal-summary__grade-label';
		chipLabel.textContent = grade.label;
		chip.appendChild(chipLabel);
		this.summaryEl.appendChild(chip);

		// Primary metrics
		const primary = document.createElement('div');
		primary.className = 'lb-deal-summary__primary';
		primary.appendChild(this.summaryMetric('Projected Profit', formatCurrency(m.projectedProfit), m.projectedProfit > 0 ? 'positive' : m.projectedProfit < 0 ? 'negative' : 'neutral'));
		primary.appendChild(this.summaryMetric('ROI', `${m.projectedROI.toFixed(1)}%`, m.projectedROI > 0 ? 'positive' : m.projectedROI < 0 ? 'negative' : 'neutral'));
		primary.appendChild(this.summaryMetric('Total Cash in Deal', formatCurrency(m.totalCashInDeal), 'neutral'));
		this.summaryEl.appendChild(primary);

		// Secondary breakdown
		const breakdown = document.createElement('div');
		breakdown.className = 'lb-deal-summary__breakdown';
		const isFlip = !!this.state.isFlip;
		const items: Array<[string, string]> = [
			['Acquisition', formatCurrency(m.totalAcquisition)],
			[mode === 'build' ? 'Build' : 'Renovation', formatCurrency(m.totalRenoCost)],
			['Holding', formatCurrency(m.totalHoldingCosts)],
			[isFlip ? 'Selling' : 'Hold strat.', isFlip ? formatCurrency(m.totalSellingCosts) : '—'],
			[mode === 'build' ? 'ACV' : 'ARV', formatCurrency(m.adjustedARV)],
		];
		items.forEach(([label, value]) => {
			const cell = document.createElement('div');
			cell.className = 'lb-deal-summary__sub';
			const k = document.createElement('div');
			k.className = 'lb-deal-summary__sub-label';
			k.textContent = label;
			cell.appendChild(k);
			const v = document.createElement('div');
			v.className = 'lb-deal-summary__sub-value';
			v.textContent = value;
			cell.appendChild(v);
			breakdown.appendChild(cell);
		});
		this.summaryEl.appendChild(breakdown);
	}

	private summaryMetric(label: string, value: string, tone: 'positive' | 'negative' | 'neutral'): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = `lb-deal-summary__metric lb-deal-summary__metric--${tone}`;
		const lbl = document.createElement('div');
		lbl.className = 'lb-deal-summary__metric-label';
		lbl.textContent = label;
		wrap.appendChild(lbl);
		const val = document.createElement('div');
		val.className = 'lb-deal-summary__metric-value';
		val.textContent = value;
		wrap.appendChild(val);
		return wrap;
	}

	// Hook the Acquisition panel needs — read the live in-memory state
	// instead of routing every read through update().
	getState(): IDealState {
		return this.state;
	}

	// Public hooks for panel code. Each panel calls `update()` with a
	// partial state diff; we mutate the in-memory state, re-render the
	// summary, broadcast to every panel so cross-panel state (the
	// linked toggle in ARV ↔ leverage Value/sqft, mode-aware labels,
	// derived totals shown in multiple panels) stays consistent
	// without requiring the calling panel to know which other panels
	// are affected, and queue a debounced PUT.
	update(patch: Partial<IDealState>): void {
		this.state = {...this.state, ...patch};
		this.renderHeaderMeta();
		this.renderSummary();
		this.broadcastToPanels();
		if (this.deal) {
			this.writer.scheduleUpdate(this.deal.slug, {stateJson: this.state});
		}
	}

	private broadcastToPanels(): void {
		// Re-render every panel that's been mounted. Each panel is
		// designed to be idempotent on render — values update, no DOM
		// rebuilding — so this is cheap and keeps cross-panel UI
		// (linked badge, ARV composition, contingency totals, soundness
		// rows, etc.) in lockstep.
		if (this.acquisitionPanel) {
			this.acquisitionPanel.render();
		}
		if (this.renoPanel) {
			this.renoPanel.render();
		}
		if (this.holdingPanel) {
			this.holdingPanel.render();
		}
		if (this.arvPanel) {
			this.arvPanel.render();
		}
		if (this.exitPanel) {
			this.exitPanel.render();
		}
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}

// ─────────── Compare view (Phase 5B + 5E) ───────────
// Reads N deal slugs (2-6) from `data-deal-slugs`, loads them via
// `/api/deals/state/`, computes metrics, renders one pane per deal
// with synced scroll, per-metric winner indicators, and (Phase 5E)
// sortable columns that re-rank the cohort left-to-right.
//
// "Winner" rules: higher is better for projectedProfit, projectedROI,
// adjustedARV, profitMarginPct. Lower is better for totalCashInDeal,
// totalAcquisition, totalRenoCost, totalHoldingCosts, ltcPct, ltvPct.
// Ties (multiple deals at the same leading value) get no highlight.
// `hideIfBothZero` extends to N panes: a metric row is dropped if
// every pane's value is zero (e.g. selling costs on hold strategies).
//
// Live in-pane editing is intentionally deferred — every panel
// hardcodes `getElementById` calls, so per-instance DOM scoping would
// require a panel refactor. The compare URL and template stay stable
// so a future slice can swap to live editing without breaking routes.

interface ICompareMetricRule {
	label: string;
	key: keyof import('./calculations').IDealMetrics;
	formatter: (n: number) => string;
	// 'higher' = bigger is better; 'lower' = smaller is better.
	direction: 'higher' | 'lower';
	// Hide entirely when every pane's value is zero (e.g. costs that
	// don't apply to a hold strategy). Optional.
	hideIfBothZero?: boolean;
}

interface ICohortMember {
	slug: DealSlug;
	deal: IDeal;
	state: IDealState;
	metrics: import('./calculations').IDealMetrics;
	// True when the deal has no underwriting inputs (no list price, no
	// purchase price, no ARV). Phase 5E UI cleanup: empty drafts skip
	// winner computation (they shouldn't compete on $0 metrics) and
	// render metrics as `—` instead of misleading negatives.
	isEmpty: boolean;
}

function isCohortDealEmpty(state: IDealState): boolean {
	return (state.listPrice || 0) === 0
		&& (state.purchasePrice || 0) === 0
		&& (state.baseARV || 0) === 0;
}

const COMPARE_PRIMARY_METRICS: ReadonlyArray<ICompareMetricRule> = [
	{label: 'Projected Profit', key: 'projectedProfit', formatter: formatCurrency, direction: 'higher'},
	{label: 'ROI', key: 'projectedROI', formatter: pctOneDecimal, direction: 'higher'},
	{label: 'Total Cash in Deal', key: 'totalCashInDeal', formatter: formatCurrency, direction: 'lower'},
];

const COMPARE_SECONDARY_METRICS: ReadonlyArray<ICompareMetricRule> = [
	{label: 'Acquisition', key: 'totalAcquisition', formatter: formatCurrency, direction: 'lower'},
	{label: 'Reno / Build', key: 'totalRenoCost', formatter: formatCurrency, direction: 'lower'},
	{label: 'Holding', key: 'totalHoldingCosts', formatter: formatCurrency, direction: 'lower'},
	{label: 'Selling', key: 'totalSellingCosts', formatter: formatCurrency, direction: 'lower', hideIfBothZero: true},
	{label: 'ARV / ACV', key: 'adjustedARV', formatter: formatCurrency, direction: 'higher'},
	{label: 'Profit margin', key: 'profitMarginPct', formatter: pctOneDecimal, direction: 'higher'},
	{label: 'LTC', key: 'ltcPct', formatter: pctOneDecimal, direction: 'lower', hideIfBothZero: true},
	{label: 'LTV', key: 'ltvPct', formatter: pctOneDecimal, direction: 'lower', hideIfBothZero: true},
];

function pctOneDecimal(n: number): string {
	if (!Number.isFinite(n)) {
		return '0.0%';
	}
	return `${n.toFixed(1)}%`;
}

// Returns the indices of cohort members that hold the leading value
// for a metric under the given direction. Ties produce multiple
// winners (or zero, if all values are equal — in which case no
// highlight is appropriate). `null` means non-finite values across
// the board.
function findCohortWinners(values: ReadonlyArray<number>, direction: 'higher' | 'lower'): Set<number> | null {
	const finite = values.map(v => Number.isFinite(v) ? v : null);
	if (finite.every(v => v === null)) {
		return null;
	}
	let leader: number | null = null;
	finite.forEach(v => {
		if (v === null) {
			return;
		}
		if (leader === null) {
			leader = v;
		} else if (direction === 'higher' && v > leader) {
			leader = v;
		} else if (direction === 'lower' && v < leader) {
			leader = v;
		}
	});
	if (leader === null) {
		return null;
	}
	// All tied → no winner highlight (Phase 5B contract carried into 5E).
	const distinct = new Set(finite.filter(v => v !== null));
	if (distinct.size <= 1) {
		return new Set<number>();
	}
	const out = new Set<number>();
	finite.forEach((v, i) => {
		if (v === leader) {
			out.add(i);
		}
	});
	return out;
}

export class DealCalcCompareView {
	private storage: HTTPDealStorage = new HTTPDealStorage();
	private rootEl: HTMLElement | null;
	private bodyEl: HTMLElement | null;
	private slugs: Array<DealSlug>;
	private cohort: Array<ICohortMember> = [];
	// Sort state: clicking a metric header re-ranks the cohort left-to-
	// right by that metric. `null` means "render in URL order."
	private sortKey: keyof import('./calculations').IDealMetrics | null = null;
	private sortDirection: 'asc' | 'desc' = 'desc';
	// Track scroll listeners so we can detach them on re-render.
	private scrollListeners: Array<() => void> = [];

	constructor() {
		this.rootEl = document.getElementById('id_lb-deal-compare');
		this.bodyEl = document.getElementById('id_lb-deal-compare-body');
		const raw = (this.rootEl && this.rootEl.getAttribute('data-deal-slugs')) || '';
		this.slugs = raw.split(',').map(s => s.trim()).filter(Boolean);
		void this.hydrate();
	}

	private async hydrate(): Promise<void> {
		const stored = await this.storage.loadState();
		if (!stored) {
			return;
		}
		// Materialize the cohort in URL order. Missing slugs (e.g. a
		// deal that was deleted between page load and hydrate) get
		// dropped silently — the surface degrades to whatever is left.
		this.cohort = this.slugs
			.map(slug => stored.deals[slug])
			.filter((d): d is IDeal => Boolean(d))
			.map(deal => {
				const state = mergeStateWithTemplate(deal.stateJson);
				return {
					slug: deal.slug,
					deal,
					state,
					metrics: computeDealMetrics(state),
					isEmpty: isCohortDealEmpty(state),
				};
			});
		this.render();
	}

	private orderedCohort(): ReadonlyArray<ICohortMember> {
		if (!this.sortKey) {
			return this.cohort;
		}
		const key = this.sortKey;
		const dir = this.sortDirection === 'desc' ? -1 : 1;
		return this.cohort.slice().sort((a, b) => {
			const va = Number(a.metrics[key] ?? 0);
			const vb = Number(b.metrics[key] ?? 0);
			if (va < vb) return -1 * dir;
			if (va > vb) return 1 * dir;
			return 0;
		});
	}

	private toggleSort(key: keyof import('./calculations').IDealMetrics, defaultDir: 'asc' | 'desc'): void {
		if (this.sortKey === key) {
			this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
		} else {
			this.sortKey = key;
			this.sortDirection = defaultDir;
		}
		this.render();
	}

	private render(): void {
		if (!this.bodyEl) {
			return;
		}
		// Detach prior scroll listeners.
		this.scrollListeners.forEach(detach => detach());
		this.scrollListeners = [];
		this.bodyEl.textContent = '';
		this.bodyEl.style.gridTemplateColumns = `repeat(${this.cohort.length}, minmax(0, 1fr))`;
		this.bodyEl.classList.toggle('lb-deal-compare__body--cohort', this.cohort.length > 2);
		const ordered = this.orderedCohort();
		const panes: Array<HTMLElement> = [];
		ordered.forEach((m, paneIdx) => {
			const pane = this.renderPane(m, paneIdx, ordered);
			this.bodyEl!.appendChild(pane);
			panes.push(pane);
		});
		this.bindSyncedScroll(panes);
	}

	private renderPane(
		member: ICohortMember,
		paneIdx: number,
		ordered: ReadonlyArray<ICohortMember>,
	): HTMLElement {
		const pane = document.createElement('section');
		pane.className = 'lb-deal-compare__pane';
		pane.setAttribute('data-pane-slug', member.slug);
		pane.id = `id_lb-deal-compare-pane-${member.slug}`;
		const head = document.createElement('header');
		head.className = 'lb-deal-compare__pane-head';
		this.fillHead(head, member, paneIdx);
		pane.appendChild(head);
		const summary = document.createElement('div');
		summary.className = 'lb-deal-compare__summary';
		this.fillSummary(summary, member, ordered);
		pane.appendChild(summary);
		const panels = document.createElement('div');
		panels.className = 'lb-deal-compare__panels';
		this.fillSecondary(panels, member, ordered);
		pane.appendChild(panels);
		return pane;
	}

	private fillHead(host: HTMLElement, member: ICohortMember, paneIdx: number): void {
		host.textContent = '';
		// Title row: inline rank prefix + display title. The rank
		// prefix is part of the title flow rather than absolute-
		// positioned, so it never overlaps the address on narrow
		// cohort panes.
		const titleRow = document.createElement('div');
		titleRow.className = 'lb-deal-compare__pane-title-row';
		const rank = document.createElement('span');
		rank.className = 'lb-deal-compare__pane-rank';
		rank.textContent = `#${paneIdx + 1}`;
		titleRow.appendChild(rank);
		const title = document.createElement('span');
		title.className = 'lb-deal-compare__pane-title';
		title.textContent = member.state.property.address || member.deal.title || member.slug;
		titleRow.appendChild(title);
		host.appendChild(titleRow);

		const meta = document.createElement('div');
		meta.className = 'lb-deal-compare__pane-meta';
		const modeLabel = member.state.mode === 'build' ? 'New Build' : 'Renovation';
		// Build the meta like the list view: mode + (scenario-name if
		// a real differentiator) + slug. Skips a title that just
		// echoes the address back (which would duplicate the title
		// row visible above this meta).
		const addressDisplay = (member.state.property && member.state.property.address) || '';
		const dealTitle = (member.deal.title || '').trim();
		const scenarioPart = dealTitle
			&& dealTitle !== addressDisplay
			&& dealTitle !== 'Untitled scenario'
			? ` · ${dealTitle}`
			: '';
		const emptyTail = member.isEmpty ? ' · Empty draft' : '';
		meta.textContent = `${modeLabel}${scenarioPart} · ${member.slug}${emptyTail}`;
		host.appendChild(meta);

		const open = document.createElement('a');
		open.className = 'lb-deal-compare__pane-open';
		open.href = `/deals/${encodeURIComponent(member.slug)}/`;
		open.innerHTML = '<span>Open editor</span><span class="material-icons" aria-hidden="true">arrow_forward</span>';
		host.appendChild(open);
	}

	private fillSummary(
		host: HTMLElement,
		member: ICohortMember,
		ordered: ReadonlyArray<ICohortMember>,
	): void {
		const chip = document.createElement('div');
		const chipKicker = document.createElement('div');
		chipKicker.className = 'lb-deal-summary__grade-kicker';
		const chipLabel = document.createElement('div');
		chipLabel.className = 'lb-deal-summary__grade-label';
		if (member.isEmpty) {
			// Empty drafts get a neutral chip instead of the misleading
			// red "PASS" / "-100% ROI" that the grade tier produces on
			// an all-zero deal.
			chip.className = 'lb-deal-summary__grade lb-deal-summary__grade--neutral';
			chipKicker.textContent = 'Not yet underwritten';
			chipLabel.textContent = 'EMPTY DRAFT';
		} else {
			const grade = gradeDeal(member.metrics.projectedROI);
			chip.className = `lb-deal-summary__grade lb-deal-summary__grade--${grade.color}`;
			chipKicker.textContent = 'Deal status';
			chipLabel.textContent = grade.label;
		}
		chip.appendChild(chipKicker);
		chip.appendChild(chipLabel);
		host.appendChild(chip);
		const primary = document.createElement('div');
		primary.className = 'lb-deal-summary__primary';
		COMPARE_PRIMARY_METRICS.forEach(rule => {
			primary.appendChild(this.metricCell(rule, member, ordered));
		});
		host.appendChild(primary);
	}

	private fillSecondary(
		host: HTMLElement,
		member: ICohortMember,
		ordered: ReadonlyArray<ICohortMember>,
	): void {
		const breakdown = document.createElement('div');
		breakdown.className = 'lb-deal-summary__breakdown lb-deal-compare__breakdown';
		const eligible = ordered.filter(m => !m.isEmpty);
		COMPARE_SECONDARY_METRICS.forEach(rule => {
			if (rule.hideIfBothZero) {
				// Check eligible (non-empty) deals only — an all-zero
				// across only the empty drafts shouldn't hide a row
				// that real deals use.
				const pool = eligible.length > 0 ? eligible : ordered;
				const allZero = pool.every(m => (m.metrics[rule.key] as number) === 0);
				if (allZero) {
					return;
				}
			}
			breakdown.appendChild(this.metricCell(rule, member, ordered));
		});
		host.appendChild(breakdown);
	}

	private metricCell(
		rule: ICompareMetricRule,
		member: ICohortMember,
		ordered: ReadonlyArray<ICohortMember>,
	): HTMLElement {
		// Empty drafts opt out of winner competition — they shouldn't
		// "win" lowest-cost metrics just by virtue of being $0.
		const eligible = ordered.filter(m => !m.isEmpty);
		const values = eligible.map(m => Number(m.metrics[rule.key] ?? 0));
		const winners = findCohortWinners(values, rule.direction);
		const myEligibleIdx = eligible.findIndex(m => m.slug === member.slug);
		const isWinner = !member.isEmpty
			&& winners !== null
			&& myEligibleIdx >= 0
			&& winners.has(myEligibleIdx)
			&& winners.size > 0
			&& winners.size < eligible.length;
		const cell = document.createElement('div');
		cell.className = 'lb-deal-summary__sub lb-deal-compare__metric';
		if (isWinner) {
			cell.classList.add('lb-deal-compare__metric--winner');
		}
		if (member.isEmpty) {
			cell.classList.add('lb-deal-compare__metric--empty');
		}
		// Phase 5E: the metric label becomes a click target that
		// re-ranks the cohort by this metric. Direction defaults to
		// the metric's `direction` (higher → desc, lower → asc).
		const k = document.createElement('button');
		k.type = 'button';
		k.className = 'lb-deal-summary__sub-label lb-deal-compare__sort-btn';
		const defaultDir: 'asc' | 'desc' = rule.direction === 'higher' ? 'desc' : 'asc';
		const isActive = this.sortKey === rule.key;
		if (isActive) {
			k.classList.add('lb-deal-compare__sort-btn--active');
			k.classList.add(`lb-deal-compare__sort-btn--${this.sortDirection}`);
		}
		k.textContent = rule.label;
		k.addEventListener('click', () => this.toggleSort(rule.key, defaultDir));
		cell.appendChild(k);
		const v = document.createElement('div');
		v.className = 'lb-deal-summary__sub-value';
		// Empty drafts render `—` instead of `$0` / `-100.0%` so users
		// don't read them as real failing metrics.
		v.textContent = member.isEmpty
			? '—'
			: rule.formatter(Number(member.metrics[rule.key] ?? 0));
		cell.appendChild(v);
		return cell;
	}

	private bindSyncedScroll(panes: ReadonlyArray<HTMLElement>): void {
		if (panes.length < 2) {
			return;
		}
		// Proportional sync — `scrollTop / (scrollHeight - clientHeight)`
		// transferred from whichever pane the user scrolled to every
		// other pane. `suppressed` prevents an infinite ping-pong when
		// our own `scrollTop` write fires the listener.
		let suppressed = false;
		panes.forEach(src => {
			const handler = () => {
				if (suppressed) {
					return;
				}
				const denom = src.scrollHeight - src.clientHeight;
				if (denom <= 0) {
					return;
				}
				const pct = src.scrollTop / denom;
				suppressed = true;
				panes.forEach(dst => {
					if (dst === src) {
						return;
					}
					const otherDenom = dst.scrollHeight - dst.clientHeight;
					dst.scrollTop = otherDenom > 0 ? pct * otherDenom : 0;
				});
				window.requestAnimationFrame(() => {
					suppressed = false;
				});
			};
			src.addEventListener('scroll', handler);
			this.scrollListeners.push(() => src.removeEventListener('scroll', handler));
		});
	}
}
